import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { Paper, Grid, TextField as TextFieldMaterial } from '@mui/material';
import { FORM_ERROR } from 'final-form';
import { Form, Field } from 'react-final-form';
import Box from '@mui/material/Box';
import { setValue } from '../helpers/finalFormMutators';
import snackbarHelper from '../helpers/snackbarHelper';
import * as errorsHelper from '../helpers/errors';
import SelectPrimaryContactUser, {
  getPrimaryContactUserForm,
  getSelectPrimaryContactUserValidationErrors,
} from './SelectPrimaryContactUser';
import LocationNotes from './Common/LocationNotes';
import OrganizationTypeSelect from './Common/OrganizationTypeSelect';
import SubmitWithSendEmailConfirmation from './SubmitWithSendEmailConfirmation';
import AddressField from './AddressField';
import Bluebird from 'bluebird';
import get from 'lodash/get';
import errorMessages from '../assets/errorMessages';
import RenderFormErrorText from './Form/RenderFormErrorText';
import { getFormFieldError, isFormFieldInvalid, isPhoneMaskValid } from '../helpers/validators';
import { getPhoneNumberFromMask } from '../helpers/getters';
import PhoneTypeSelect from './PhoneTypeSelect';
import { PHONE_EXT_MAX_LENGTH } from './Form/MuiFormWrapper';
import ZipCodeSelect from './ZipCodeSelect';
import CheckboxField from './Common/CheckboxField';
import PhoneMask from './PhoneMask';
import RFFTextField from './Common/RFFTextField';
import LogoUploadField from './LogoUploadField';

class ReceiverLocationForm extends Component {
  constructor(props) {
    super(props);

    this.formRef = createRef();
    this.state = {
      selectedUser: props.user,
      handleCreateNewTypeFieldFlag: undefined,
      sendInvitationEmail: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { user } = this.props;

    // update selectedUser after form submit
    if (get(prevProps, 'user.id') !== get(user, 'id')) {
      this.setState({ selectedUser: user });
    }
  }

  handleSubmit = values => {
    const { onSubmit } = this.props;
    const { sendInvitationEmail } = this.state;

    return Bluebird
      .try(() => onSubmit({
        ...values,
        send_invitation: sendInvitationEmail,
      }))
      .then(() => snackbarHelper.success('Receiving Agency profile was updated successfully!'))
      .catch(err => {
        let formErrors = {
          [FORM_ERROR]: 'Receiving Agency update failed',
        };

        switch (err.code) {
          case errorMessages.ERR_DUPLICATED_PHONE.code:
            formErrors = {
              ...formErrors,
              primary_contact_phone: errorMessages.ERR_DUPLICATED_PHONE.message,
            };
            break;
          case errorMessages.ERR_DUPLICATED_EMAIL.code:
            formErrors = {
              ...formErrors,
              primary_contact_email: errorMessages.ERR_DUPLICATED_EMAIL.message,
            };
            break;
          default:
            errorsHelper.handleError(err);

            break;
        }

        return formErrors;
      });
  };

  validate = values => {
    const { handleCreateNewTypeFieldFlag } = this.state;
    const errors = {};

    if (!values.receiver_name) {
      errors.receiver_name = 'Required';
    }

    if (!values.type_id) {
      errors.type_id = 'Required';
    }

    if(getPhoneNumberFromMask(values.phone).length > 0 && !isPhoneMaskValid(values.phone)) {
      errors.phone = errorMessages.ERR_INVALID_PHONE.message;
    }

    if (handleCreateNewTypeFieldFlag && !values.type_other) {
      errors.type_other = 'Required';
    }

    if (!values.address1) {
      errors.address1 = 'Required';
    }

    if (!values.city) {
      errors.city = 'Required';
    }

    if (!values.st) {
      errors.st = 'Required';
    }

    if (!values.zip) {
      errors.zip = 'Required';
    }

    return {
      ...errors,
      ...getSelectPrimaryContactUserValidationErrors(values),
    };
  };

  render() {
    const { selectedUser } = this.state;
    const { location, receiverTypes, receiver, showNotes, onLogoSave } = this.props;

    const initialValues = {
      ...location,
      receiver_name: receiver.name,
      about: receiver.about,
      type_id: receiver.type_id,
      type_other: receiver.type_other,
      ein: receiver.ein,
      active: receiver.active,
      logo_url: receiver.logo_url,
      ...getPrimaryContactUserForm(selectedUser),
    };
    const buttonText = location ? 'Update Receiving Agency' : 'Create Receiving Agency';

    return (
      <Paper sx={{ padding: { xs: 1, sm: 2 } }} data-testid="receiver-location-edit-form">
        <Form
          mutators={{
            ...setValue,
          }}
          onSubmit={this.handleSubmit}
          validate={this.validate}
          initialValues={initialValues}
          // the logo is saved on its own (see LogoUploadField), which refreshes the receiver, keep the unsaved edits
          keepDirtyOnReinitialize
          render={({
            handleSubmit,
            submitting,
            dirty,
            pristine,
            form,
            values,
          }) => (
            <form style={{ maxWidth: '500px' }} onSubmit={handleSubmit} ref={this.formRef} data-testid="receiver-edit-form">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <LogoUploadField alt={values.receiver_name} onSave={onLogoSave} />
                </Grid>

                <Grid item xs={12}>
                  <RFFTextField
                    name="receiver_name"
                    fullWidth
                    required
                    type="text"
                    label="Receiving Agency Name"
                    autoFocus
                    variant="standard"
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 1.5 }}>
                  <RFFTextField
                    name="about"
                    fullWidth
                    multiline
                    label="About"
                    placeholder="Tell us about your work!"
                    rows={3}
                    inputProps={{ maxLength: 200 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <OrganizationTypeSelect
                    selectedTypeId={initialValues.type_id}
                    list={receiverTypes}
                    handleCreateNewTypeFieldFlag={value => this.setState({ handleCreateNewTypeFieldFlag: value })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RFFTextField name="ein" fullWidth label="EIN (optional)" variant="standard" />
                </Grid>

                <Grid item xs={12}>
                  <AddressField
                    name="address1"
                    fullWidth
                    required
                    component={RFFTextField}
                    type="text"
                    label="Address"
                  />
                </Grid>

                <Grid item xs={12}>
                  <AddressField
                    fullWidth
                    variant="standard"
                    name="zip"
                    required
                    formValue={values.zip}
                    onChangeZipCode={(value) => {
                      form.mutators.setValue('zip', value ? value.data.zip_code : '');
                      form.mutators.setValue('city', value ? value.data.city : '');
                      form.mutators.setValue('st', value ? value.data.state : '');
                    }}
                    showValueOnly
                    component={ZipCodeSelect}
                    type="text"
                    label="Zip Code"
                  />
                </Grid>

                <Grid item xs={12}>
                  <AddressField
                    name="city"
                    fullWidth
                    required
                    component={RFFTextField}
                    type="text"
                    label="City"
                    disabled={!!values.zip}
                  />
                </Grid>

                <Grid item xs={12}>
                  <AddressField
                    name="county"
                    fullWidth
                    component={RFFTextField}
                    type="text"
                    label="County"
                  />
                </Grid>

                <Grid item xs={12}>
                  <AddressField
                    name="st"
                    fullWidth
                    required
                    component={RFFTextField}
                    type="text"
                    label="State"
                    disabled={!!values.zip}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    name="phone"
                    render={({ input, meta }) => (
                      <TextFieldMaterial
                        variant="standard"
                        name={input.name}
                        fullWidth
                        label="Phone Number, e.g.: 555-555-5555"
                        margin="normal"
                        style={{ marginTop: 0, marginBottom: 0 }}
                        error={isFormFieldInvalid(meta)}
                        helperText={isFormFieldInvalid(meta) ? getFormFieldError(meta) : null}
                        value={input.value}
                        onChange={event => input.onChange(event)}
                        onFocus={event => input.onFocus(event)}
                        onBlur={event => input.onBlur(event)}
                        InputProps={{
                          inputComponent: PhoneMask,
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <AddressField
                    fullWidth
                    name="phone_ext"
                    InputProps={{
                      inputProps: {
                        maxLength: PHONE_EXT_MAX_LENGTH,
                      },
                    }}
                    component={RFFTextField}
                    type="text"
                    label="Phone Number Extension"
                  />
                </Grid>

                <Grid item xs={12}>
                  <PhoneTypeSelect value={values.phone_type} />
                </Grid>

                <Grid item xs={12}>
                  <SelectPrimaryContactUser
                    values={values}
                    formApi={form}
                    firstNameLabel="Rescues Contact Person: First Name *"
                    lastNameLabel="Rescues Contact Person: Last Name *"
                    emailLabel="Rescues Contact Person: Email *"
                    phoneLabel="Rescues Contact Person: Phone#"
                    helpText="Select the person to contact to coordinate details about rescues"
                    showZipCodeField={false}
                    showUserSelector
                    onUserSelect={() => {
                      const elem = document.querySelector(`[name='notes']`);
                      return elem && elem.focus();
                    }}
                  />
                </Grid>

                {showNotes && (
                  <Grid item xs={12}>
                    <Box my={1} width="100%">
                      <LocationNotes tooltipLabel="These notes are like a post-it note about the Receiving Agency" />
                    </Box>
                  </Grid>
                )}


                <Grid item xs={12}>
                  <CheckboxField name="active" color="primary" label="Is Accepting Donations?" labelPlacement="end" />
                </Grid>

                <Grid item xs={12}>
                  <SubmitWithSendEmailConfirmation
                    data-testid="submit-edit-receiver-location"
                    label={buttonText}
                    showConfirmation={
                      !values.primary_contact_id &&
                      Object.keys(getSelectPrimaryContactUserValidationErrors(values)).length === 0
                    } // invite new user?
                    disabled={pristine || !dirty || submitting}
                    sx={{ marginTop: 1 }}
                    onButtonNotSend={() => {
                      this.setState({ sendInvitationEmail: false }, () => {
                        this.formRef.current.dispatchEvent(
                          new Event('submit', {
                            bubbles: true,
                            cancelable: true,
                          })
                        );
                      });
                    }}
                    onButtonSend={() => {
                      this.setState({ sendInvitationEmail: true }, () => {
                        this.formRef.current.dispatchEvent(
                          new Event('submit', {
                            bubbles: true,
                            cancelable: true,
                          })
                        );
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <RenderFormErrorText formState={form.getState()} />
                </Grid>
              </Grid>
            </form>
          )}
        />
      </Paper>
    );
  }
}

ReceiverLocationForm.propTypes = {
  user: PropTypes.object,
  showNotes: PropTypes.bool,
};

export default ReceiverLocationForm;
